import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { carriers } from '@/api/carriers';
import type { CarrierRow, CarrierScope, CarrierKind } from '@/api/carriers';
import { freeswitch } from '@/api/freeswitch';
import type { GatewayState } from '@/api/freeswitch';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { CarrierDialog } from './CarrierDialog';
import { formatRef } from '@/lib/formatRef';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/cn';
import {
  Plus, MoreHorizontal, Trash2, Pencil, RefreshCw, Radio, AlertTriangle,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';

// ── Role constants ─────────────────────────────────────────────────────────────

const CAN_MUTATE = ['super_admin', 'reseller_owner', 'office_manager'];
const CAN_SYNC   = ['super_admin'];

// ── Kind badge ─────────────────────────────────────────────────────────────────

function KindBadge({ kind }: { kind: CarrierKind }) {
  const styles: Record<CarrierKind, string> = {
    voice:  'bg-blue-50 text-blue-700 border-blue-200',
    sms:    'bg-purple-50 text-purple-700 border-purple-200',
    both:   'bg-indigo-50 text-indigo-700 border-indigo-200',
  };
  const labels: Record<CarrierKind, string> = {
    voice: 'Voice',
    sms: 'SMS',
    both: 'Both',
  };
  return (
    <span
      className={cn(
        'inline-block rounded border px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider leading-none',
        styles[kind],
      )}
    >
      {labels[kind]}
    </span>
  );
}

// ── Scope badge ────────────────────────────────────────────────────────────────

function ScopeBadge({ scope }: { scope: CarrierScope }) {
  const styles: Record<CarrierScope, string> = {
    platform:  'bg-slate-100 text-slate-700 border-slate-300',
    reseller:  'bg-indigo-50 text-indigo-700 border-indigo-200',
    customer:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  };
  return (
    <span
      className={cn(
        'inline-block rounded border px-1.5 py-0.5 text-[11px] font-semibold capitalize tracking-wider leading-none',
        styles[scope],
      )}
    >
      {scope}
    </span>
  );
}

// ── Provider pill ─────────────────────────────────────────────────────────────

function ProviderPill({ provider }: { provider: string }) {
  return (
    <span className="inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-mono text-slate-700 border border-slate-200">
      {provider}
    </span>
  );
}

// ── Gateway status pill ────────────────────────────────────────────────────────
// Maps GatewayState → a colored pill

const STATE_CLASS: Record<GatewayState, string> = {
  REGED:     'bg-green-50 text-green-700 border-green-200',
  NOREG:     'bg-amber-50 text-amber-700 border-amber-200',
  UNREG:     'bg-amber-50 text-amber-700 border-amber-200',
  TRYING:    'bg-blue-50 text-blue-700 border-blue-200',
  FAILED:    'bg-red-50 text-red-700 border-red-200',
  FAIL_WAIT: 'bg-red-50 text-red-700 border-red-200',
  DOWN:      'bg-slate-100 text-slate-500 border-slate-200',
  UNKNOWN:   'bg-slate-50 text-slate-400 border-slate-200',
};

function GatewayPill({ state }: { state: GatewayState | null }) {
  const { t } = useTranslation();
  const STATE_LABEL: Record<GatewayState, string> = {
    REGED:     t('carriers.gatewayRegistered'),
    NOREG:     t('carriers.gatewayNotRegistered'),
    UNREG:     t('carriers.gatewayNotRegistered'),
    TRYING:    t('carriers.gatewayConnecting'),
    FAILED:    t('carriers.gatewayFailed'),
    FAIL_WAIT: t('carriers.gatewayFailed'),
    DOWN:      t('carriers.gatewayDown'),
    UNKNOWN:   '—',
  };
  if (!state) {
    return <span className="text-slate-400 text-sm">—</span>;
  }
  return (
    <span
      className={cn(
        'inline-block rounded border px-1.5 py-0.5 text-[11px] font-medium leading-none',
        STATE_CLASS[state] ?? STATE_CLASS.UNKNOWN,
      )}
    >
      {STATE_LABEL[state] ?? '—'}
    </span>
  );
}

// ── Active toggle ─────────────────────────────────────────────────────────────

function ActiveToggle({ row, canMutate }: { row: CarrierRow; canMutate: boolean }) {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: () => carriers.update(row.id, { active: !row.active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['carriers'] });
    },
    onError: (err) => {
      toast({
        title: 'Failed to toggle carrier',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });
  return (
    <Switch
      checked={row.active}
      onCheckedChange={() => toggle.mutate()}
      disabled={!canMutate || toggle.isPending}
      aria-label={row.active ? 'Deactivate carrier' : 'Activate carrier'}
    />
  );
}

// ── Delete confirm dialog ─────────────────────────────────────────────────────

function DeleteDialog({ row, onClose }: { row: CarrierRow; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: () => carriers.remove(row.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['carriers'] });
      qc.invalidateQueries({ queryKey: ['freeswitch', 'status'] });
      toast({ title: t('carriers.deleteTitle'), description: `"${row.name}" has been removed.` });
      onClose();
    },
    onError: (err) => {
      toast({
        title: t('carriers.deleteFailed'),
        description: err instanceof Error ? err.message : t('common.error'),
        variant: 'destructive',
      });
    },
  });
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('carriers.deleteTitle')}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          {t('carriers.deleteBody', { name: row.name })}
        </p>
        {del.isError && (
          <p role="alert" className="text-xs text-red-500">
            {del.error instanceof Error ? del.error.message : t('carriers.deleteFailed')}
          </p>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={del.isPending}>{t('common.cancel')}</Button>
          </DialogClose>
          <Button variant="destructive" disabled={del.isPending} onClick={() => del.mutate()}>
            {del.isPending ? t('common.deleting') : t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function CarriersList() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const qc = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [editCarrier, setEditCarrier] = useState<CarrierRow | null>(null);
  const [deleteCarrier, setDeleteCarrier] = useState<CarrierRow | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState<CarrierScope | 'all'>('all');
  const [kindFilter, setKindFilter] = useState<CarrierKind | 'all'>('all');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isSuperAdmin = user.is_super_admin;
  const canMutate = CAN_MUTATE.includes(user.role);
  const canSync = CAN_SYNC.includes(user.role);

  function handleSearchChange(v: string) {
    setSearch(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(v), 300);
  }

  // ── Carriers query ─────────────────────────────────────────────────────────

  const { data, isLoading, error } = useQuery({
    queryKey: ['carriers', debouncedSearch, scopeFilter, kindFilter],
    queryFn: () =>
      carriers.list({
        search: debouncedSearch || undefined,
        scope: scopeFilter !== 'all' ? scopeFilter : undefined,
        kind: kindFilter !== 'all' ? kindFilter : undefined,
      }),
  });

  const rows = data?.rows ?? [];

  // ── FreeSWITCH status query (super_admin only, 10s poll) ───────────────────

  const { data: fsStatus } = useQuery({
    queryKey: ['freeswitch', 'status'],
    queryFn: () => freeswitch.status(),
    refetchInterval: 10_000,
    enabled: isSuperAdmin,
  });

  function getGatewayState(carrierId: string): GatewayState | null {
    if (!isSuperAdmin || !fsStatus) return null;
    return fsStatus.gateways.find((g) => g.carrier_id === carrierId)?.state ?? 'UNKNOWN';
  }

  // ── Sync mutation ──────────────────────────────────────────────────────────

  const sync = useMutation({
    mutationFn: () => freeswitch.syncCarriers(),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['carriers'] });
      qc.invalidateQueries({ queryKey: ['freeswitch', 'status'] });
      if (result.ok) {
        toast({
          title: t('carriers.syncToFreeswitch'),
          description: `${result.gatewaysWritten} gateway${result.gatewaysWritten !== 1 ? 's' : ''} written, ${result.gatewaysDeleted} removed.`,
        });
      } else {
        toast({
          title: t('carriers.syncToFreeswitch'),
          description: result.errors[0] ?? t('common.error'),
          variant: 'destructive',
        });
      }
    },
    onError: (err) => {
      toast({
        title: t('carriers.syncToFreeswitch'),
        description: err instanceof Error ? err.message : t('common.error'),
        variant: 'destructive',
      });
    },
  });

  // ── Table columns ──────────────────────────────────────────────────────────

  const columns = [
    {
      header: t('carriers.columnRef'),
      cell: (row: CarrierRow) => (
        <span className="font-mono text-xs text-slate-500">
          {row.ref_number != null ? formatRef(row.ref_number, 'CAR') : '—'}
        </span>
      ),
    },
    {
      header: t('carriers.columnName'),
      cell: (row: CarrierRow) => (
        <span
          className={cn(
            'font-mono text-sm',
            row.active ? 'font-semibold text-foreground' : 'font-normal text-muted-foreground italic',
          )}
        >
          {row.name}
        </span>
      ),
    },
    {
      header: t('carriers.columnProvider'),
      cell: (row: CarrierRow) => <ProviderPill provider={row.provider} />,
    },
    {
      header: t('carriers.columnKind'),
      cell: (row: CarrierRow) => <KindBadge kind={row.kind} />,
    },
    {
      header: t('carriers.columnScope'),
      cell: (row: CarrierRow) => <ScopeBadge scope={row.scope} />,
    },
    {
      header: t('carriers.columnHost'),
      cell: (row: CarrierRow) => (
        <span className="font-mono text-xs text-slate-600">{row.sip_config.host}</span>
      ),
    },
    {
      header: t('carriers.columnStatus'),
      cell: (row: CarrierRow) => {
        if (!isSuperAdmin) return <span className="text-slate-400 text-sm">—</span>;
        if (fsStatus && !fsStatus.freeswitch_up) {
          return (
            <span className="inline-block rounded border px-1.5 py-0.5 text-[11px] font-medium leading-none bg-slate-100 text-slate-500 border-slate-200">
              {t('carriers.fsDdown')}
            </span>
          );
        }
        return <GatewayPill state={getGatewayState(row.id)} />;
      },
    },
    {
      header: t('carriers.columnActive'),
      cell: (row: CarrierRow) => (
        <ActiveToggle row={row} canMutate={canMutate} />
      ),
    },
    {
      header: '',
      className: 'w-10 text-end',
      cell: (row: CarrierRow) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t('common.actions')}
              onClick={(e) => e.stopPropagation()}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            {canMutate && (
              <DropdownMenuItem onClick={() => setEditCarrier(row)}>
                <Pencil className="h-3.5 w-3.5 me-2" />
                {t('common.edit')}
              </DropdownMenuItem>
            )}
            {canMutate && (
              <DropdownMenuItem
                className="text-red-600 focus:text-red-600 focus:bg-red-50"
                onClick={() => setDeleteCarrier(row)}
              >
                <Trash2 className="h-3.5 w-3.5 me-2" />
                {t('common.delete')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('carriers.title')}
        description={t('carriers.description')}
        actions={
          <div className="flex items-center gap-2">
            {/* Scope filter */}
            <Select
              value={scopeFilter}
              onValueChange={(v) => setScopeFilter(v as typeof scopeFilter)}
            >
              <SelectTrigger className="h-9 w-36 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('carriers.allScopes')}</SelectItem>
                <SelectItem value="platform">{t('carriers.platform')}</SelectItem>
                <SelectItem value="reseller">{t('carriers.reseller')}</SelectItem>
                <SelectItem value="customer">{t('carriers.customer')}</SelectItem>
              </SelectContent>
            </Select>

            {/* Kind filter */}
            <Select
              value={kindFilter}
              onValueChange={(v) => setKindFilter(v as typeof kindFilter)}
            >
              <SelectTrigger className="h-9 w-32 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('carriers.allKinds')}</SelectItem>
                <SelectItem value="voice">{t('carriers.voice')}</SelectItem>
                <SelectItem value="sms">{t('carriers.sms')}</SelectItem>
                <SelectItem value="both">{t('carriers.both')}</SelectItem>
              </SelectContent>
            </Select>

            {/* Search */}
            <Input
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t('carriers.searchPlaceholder')}
              className="h-9 w-44"
              aria-label={t('carriers.searchPlaceholder')}
            />

            {/* Sync button — super_admin only */}
            {canSync && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => sync.mutate()}
                disabled={sync.isPending}
                aria-label={t('carriers.syncToFreeswitch')}
              >
                <RefreshCw className={cn('h-4 w-4 me-1.5', sync.isPending && 'animate-spin')} />
                {t('carriers.syncToFreeswitch')}
              </Button>
            )}

            {/* Add Carrier — mutable roles only */}
            {canMutate && (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 me-1.5" />
                {t('carriers.addCarrier')}
              </Button>
            )}
          </div>
        }
      />

      {/* FreeSWITCH down banner */}
      {isSuperAdmin && fsStatus && !fsStatus.freeswitch_up && (
        <div
          role="alert"
          className="mb-4 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
          {t('carriers.freeswitchDown')}
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-slate-400">{t('common.loading')}</div>
      ) : error ? (
        <div role="alert" className="text-sm text-red-500">
          {error instanceof Error ? error.message : t('carriers.failedToLoad')}
          <Button
            variant="outline"
            size="sm"
            className="ms-3"
            onClick={() => qc.invalidateQueries({ queryKey: ['carriers'] })}
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          onRowClick={canMutate ? (row) => setEditCarrier(row) : undefined}
          empty={
            debouncedSearch
              ? t('carriers.noMatch', { query: debouncedSearch })
              : t('carriers.noCarriersYet')
          }
        />
      )}

      {/* Create dialog */}
      <CarrierDialog
        mode="create"
        open={createOpen}
        onOpenChange={setCreateOpen}
      />

      {/* Edit dialog */}
      {editCarrier && (
        <CarrierDialog
          mode="edit"
          row={editCarrier}
          open
          onOpenChange={(v) => { if (!v) setEditCarrier(null); }}
        />
      )}

      {/* Delete confirm */}
      {deleteCarrier && (
        <DeleteDialog
          row={deleteCarrier}
          onClose={() => setDeleteCarrier(null)}
        />
      )}
    </>
  );
}
